import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HelpCircle, Move } from "lucide-react";

const KEY = "cc_help_corner";
const CORNERS = {
  "bottom-right": "bottom-6 right-6",
  "bottom-left": "bottom-6 left-6 lg:left-[17rem]",
  "top-right": "top-20 right-6",
  "top-left": "top-20 left-6 lg:left-[17rem]",
};

/** Help bubble the user can drag to any corner — it snaps to the nearest one and remembers it. */
export function FloatingHelpButton() {
  const navigate = useNavigate();
  const [corner, setCorner] = useState(() => localStorage.getItem(KEY) || "bottom-right");
  const [drag, setDrag] = useState(null); // {x, y} while being dragged
  const moved = useRef(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      moved.current = true;
      setDrag({ x: e.clientX, y: e.clientY });
    };
    const onUp = (e) => {
      const next = `${e.clientY < window.innerHeight / 2 ? "top" : "bottom"}-${e.clientX < window.innerWidth / 2 ? "left" : "right"}`;
      if (moved.current) { setCorner(next); localStorage.setItem(KEY, next); }
      setDrag(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
    return () => { window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
  }, [drag]);

  const style = drag
    ? { position: "fixed", left: drag.x - 70, top: drag.y - 24, bottom: "auto", right: "auto" }
    : undefined;

  return (
    <div ref={ref} style={style} data-testid="floating-help-wrap"
      className={`fixed z-50 flex items-center gap-1.5 ${drag ? "cursor-grabbing" : CORNERS[corner] || CORNERS["bottom-right"]}`}>
      <button
        onClick={() => { if (!moved.current) navigate("/help"); moved.current = false; }}
        data-testid="floating-help-btn"
        className="flex items-center gap-2 bg-cc-orange hover:bg-orange-600 text-white font-semibold px-5 py-3 rounded-full shadow-lg shadow-orange-500/30 transition-transform hover:scale-105">
        <HelpCircle className="w-5 h-5" />
        <span className="hidden sm:inline">Help &amp; Training</span>
      </button>
      <button
        onPointerDown={(e) => { e.preventDefault(); moved.current = false; setDrag({ x: e.clientX, y: e.clientY }); }}
        title="Drag to move this button to another corner"
        data-testid="floating-help-move"
        className="w-7 h-7 rounded-full bg-white border border-zinc-200 text-zinc-400 hover:text-cc-orange shadow flex items-center justify-center cursor-grab active:cursor-grabbing touch-none">
        <Move className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
